<template>
    <div class="pl--0 pr--0 pt--10 client__item__details__sub">
        <template v-if="fetchInvoiceLoading">
            <PageSpinner />
        </template>

        <template v-else-if="fetchInvoiceErr">
            <EmptyPage state="error" @action="handleFetchInvoices" />
        </template>

        <template v-else>
            <div class="w-100 mt--20">
                <template v-if="!invoices.length">
                    <EmptyPage
                        state="empty_custom" 
                        btn-state="custom" 
                        icon-name="receipt"
                        type="invoice" 
                        position="left"
                        title="No invoices with this client" 
                        subtitle="Invoices keep track of paid projects/collaborations with clients. All invoices created for this specific client show up here."
                    >
                        <template #button>
                            <BaseButton 
                                label="+ Create invoice" 
                                @submit="goToCreateInvoice"
                            />
                        </template>
                    </EmptyPage>
                </template>

                <template v-else>
                    <div class="flex align-items-center mb--15 justify-content-between">
                        <p class="text--bold text-gray-400">
                            Invoices
                        </p>
                        <div class="flex align-items-center gap-2 flex align-items-center justify-content-end width-100">
                            <InnerPageFilter
                                v-model="filter" 
                                page-type="clients" 
                                @update-filter="handleFilterUpdate"
                            />
                            <BaseButton
                                label="+ Add invoice" 
                                :loading="upgradeLoadingDisabled" 
                                :disabled="upgradeLoadingDisabled"
                                @submit="goToCreateInvoice"
                            />
                        </div>
                    </div>

                    <div class="flex mt--20">
                        <CustomTable
                            :columns="invoiceHeaders"
                            :data="invoices"
                            router-name="invoice-details-view"
                            row-type="link" :is-checkbox="false"
                            @onRowClick="goToInvoiceDetailsPage"
                        >
                            <template #rows="data">
                                <td>
                                    <Status type="invoice" :value="data.status" />
                                </td>
                                <td class="text--upper">
                                    {{ data.currency }} {{ $format_number(data.totalAmount) }}
                                </td>
                                <td>
                                    #{{ data.invoiceNo || "-" }}
                                </td>
                                <td>{{ $format_date(data.dueDate) }}</td>
                                <td>{{ $format_date(data.createdAt) || "-" }}</td>
                            </template>
                        </CustomTable>
                    </div>

                    <div class="flex align-items-center justify-content-between">
                        <p class="text-gray-200 text--medium text--xs">
                            COUNT: <span>{{ invoices.length }}</span>
                        </p>
                        <pagination v-if="showPagination" :pagination-data="pageData" />
                    </div>
                </template>
            </div>
        </template>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || showUpgradeModalAlt" 
            type="invoices" 
            @cancel="closeUpgradeModal" 
        />
    </div>
</template>

<script>
import {
    EmptyPage,
    PageSpinner,
    BaseButton,
    CustomTable,
    Status,
    Pagination,
    UpgradeAccountModal,
    InnerPageFilter,
} from "@/components/ui/index";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import { isFilterValueEmpty } from "@/utils/helpers";

export default {
    name: "ClientInvoices",

    components: {
        CustomTable, 
        Pagination,
        EmptyPage, 
        UpgradeAccountModal,
        PageSpinner,
        BaseButton,
        Status,
        InnerPageFilter,
    },

    props: {
        currentClient: { type: Object, default: () => {} },
    },

    data() {
        return {
            fetchInvoiceLoading: false,
            fetchInvoiceErr: false,
            fetchInvoiceErrMsg: "",
            invoiceHeaders: [
                { id: "Status", accessorKey: "Status", iconName: "user"},
                { id: "Amount", accessorKey: "amount", iconName: "buildings"},
                { id: "Invoice No.", accessorKey: "invoiceNo", iconName: "reports-solid"},
                { id: "Due Date", accessorKey: "dueDate", iconName: "timer-solid"},
                { id: "Date added", accessorKey: "createdAt", iconName: "timer-solid"},
            ],
            showUpgradeModalAlt: false,
            filter: {},
        };
    },

    computed: {
        pageData() {
            return this.$store.getters["clients/currentClient"]?.pageData;
        },

        invoices() {
            return this.$store.getters["clients/clientInvoices"]?.list;
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        upgradeLoadingDisabled() {
            return this.checkUpgradeLoading && this.checkUpgradeFields.invoices;
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        userInvoicesTotal() {
            return this.$store.getters["invoice/pageData"]?.total;
        },
    },

    async mounted() {
        await this.handleFetchInvoices();
    },

    methods: {
        async handleFetchInvoices(payload) {
            try {
                this.fetchInvoiceErr = false;
                this.fetchInvoiceLoading = true;

                const id = this.$route.params.id;

                const params = {
                    page: payload?.pageNumber || this.pageData?.currentPage,
                    clientId: this.$route.params.id,
                };

                await this.$store.dispatch("clients/getClientInvoices", { id, params });
            } catch (error) {
                this.fetchInvoiceErr = true;
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.fetchInvoiceErrMsg = errMsg;
            } finally {
                this.fetchInvoiceLoading = false;
            }
        },

        async goToCreateInvoice() {
            try {
                if (["standard", "free"].includes(this.accountPlan)) {        
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].invoices?.create;
        
                    if (maxLimit !== 0 && this.userInvoicesTotal >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    } 
                }

                this.$store.dispatch("invoice/setSelectedClient", this.currentClient);
                this.$router.push({ path: "/dashboard/invoices/create", query: { from_route: "client" } });
            } catch (error) {
                // console.log({ error });
            }
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "invoices");
        },

        goToInvoiceDetailsPage(row) {
            if (!row._id) return;
            this.$router.push(`/dashboard/invoices/${row._id}`);
        },

        async handleFilterUpdate(serializedFilter) {
            const parsedFilter = JSON.parse(serializedFilter);
            const modifiedParams = {};

            this.filter = parsedFilter;            
            
            if (parsedFilter) {
                const obj = Object.entries(parsedFilter);
                    
                obj.forEach(([key, values]) => {                    
                    if (key && !isFilterValueEmpty(values.value)) {
                        modifiedParams[key] = values.value;
                    }
                });
                // await this.handleFetchInvoices(modifiedParams);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .title {
        max-width: 170px;
    }
</style>